import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { Paperclip } from 'lucide-react';
import { cn } from '../../lib/utils';
import { v4 as uuidv4 } from 'uuid';
import {
    addMRIMachine,
    updateMRIMachine,
    MRIMachineDTO,
} from '../../services/apiService';
import { SpatialGradientDTO, MaxSARDTO } from '../../models/MRIMachineModels';
import {
    MRIMachineFormData,
    MachineFormValues,
    toEquipmentInput,
} from './types';
import RoomDetailsCard from './form/RoomDetailsCard';
import ScannerCard from './form/ScannerCard';
import EquipmentCard from './form/EquipmentCard';
import {
    Category,
    categoryKey,
} from '../ui-components/category/categoryColors';
import { useToast } from '../../hooks/use-toast';
import FilePicker from '../ui-components/pickers/FilePicker';
import PolicyPicker from '../ui-components/pickers/PolicyPicker';
import { Button } from '../ui/button';
import { Form } from '../ui/form';
import FormCard from './form/FormCard';

interface MRIMachineFormProps {
    MRImachine: MRIMachineFormData;
    isNew: boolean;
    actions?: 'page' | 'panel';
    onCancel?: () => void;
    onClose?: () => void;
    onChange?: () => void;
}

export default function MRIMachineForm({
    MRImachine,
    isNew,
    actions = 'page',
    onCancel,
    onClose,
    onChange,
}: MRIMachineFormProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [renamePending, setRenamePending] = useState<{
        from: Category;
        to: Category;
    } | null>(null);

    const handleRenameCategory = (rename: { from: Category; to: Category }) =>
        setRenamePending((pending) =>
            pending && categoryKey(pending.to) === categoryKey(rename.from)
                ? { from: pending.from, to: rename.to }
                : rename
        );

    const form = useForm<MachineFormValues>({
        defaultValues: {
            Name: MRImachine.Name || '',
            Model: MRImachine.Model || '',
            Manufacturer: MRImachine.Manufacturer || '',
            FieldStrength: MRImachine.FieldStrength || 1.5,
            SpatialGradient: (MRImachine.SpatialGradient || []).map(
                (sg: SpatialGradientDTO & { id?: string; _id?: string }) => ({
                    id: sg.id || sg._id || uuidv4(),
                    diameter: sg.diameter ?? '',
                    value: sg.value ?? 0,
                })
            ),
            SlewRatePerAxis: MRImachine.SlewRatePerAxis ?? 0,
            MaxSlewRate: MRImachine.MaxSlewRate ?? 0,
            MaxSAR: (MRImachine.MaxSAR || []).map(
                (sar: MaxSARDTO & { id?: string; _id?: string }) => ({
                    id: sar.id || sar._id || uuidv4(),
                    level: sar.level ?? 0,
                    wholeBody: sar.wholeBody ?? 0,
                    head: sar.head ?? 0,
                })
            ),
            MaxB1rms: MRImachine.MaxB1rms || '',
            TRCoils: MRImachine.TRCoils || '',
            attachedFiles: MRImachine.attachedFiles || [],
            machineImage: MRImachine.machineImage || '',
            notes: MRImachine.notes || '',
            location: MRImachine.location || '',
            category: MRImachine.category ?? null,
            equipment: [],
            policies: MRImachine.policies ?? [],
        },
    });

    const onSubmit = async (data: MachineFormValues) => {
        setIsSaving(true);
        const machineData: MRIMachineDTO & { _id?: string } = {
            ...data,
            _id: MRImachine._id,
            SpatialGradient: data.SpatialGradient.map((sg) => ({
                ...sg,
                value: Number(sg.value),
            })),
            SlewRatePerAxis: Number(data.SlewRatePerAxis),
            MaxSlewRate: Number(data.MaxSlewRate),
            MaxSAR: data.MaxSAR.map((sar) => ({
                ...sar,
                level: Number(sar.level),
                wholeBody: Number(sar.wholeBody),
                head: Number(sar.head),
            })),
            machineImage: data.machineImage || null,
            equipment: data.equipment.map(({ id, ...item }) =>
                toEquipmentInput(item)
            ),
            policies: data.policies.map((p) => p._id),
            attachedFiles: data.attachedFiles.map((f) => f._id),
            renameCategory: renamePending ?? undefined,
        };

        try {
            if (isNew) {
                await addMRIMachine(machineData);
            } else {
                await updateMRIMachine(machineData);
            }
            toast({
                title: t(
                    'roomOverview.mriMachineForm.messages.successfullySaved'
                ),
            });
            onChange?.();
            if (onClose) {
                onClose();
            } else {
                navigate('/room-overview');
            }
        } catch (error) {
            console.error('Error saving machine:', error);
            toast({
                title: t('roomOverview.mriMachineForm.messages.notSaved'),
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        (e.target as HTMLElement).tagName !== 'TEXTAREA'
                    ) {
                        e.preventDefault();
                    }
                }}
                className="max-w-3xl space-y-8 [&_.space-y-2>*+*]:mt-1.5 [&_[role=combobox]]:border-control [&_[role=combobox]]:bg-background [&_input]:border-control [&_input]:bg-background [&_textarea]:border-control [&_textarea]:bg-background"
            >
                <RoomDetailsCard
                    isNew={isNew}
                    imageUrl={MRImachine.machineImageUrl}
                    onRenameCategory={handleRenameCategory}
                />
                <ScannerCard />
                {isNew && <EquipmentCard />}

                <FormCard
                    icon={Paperclip}
                    title={t(
                        'roomOverview.mriMachineForm.sections.attachments'
                    )}
                    description={t(
                        'roomOverview.mriMachineForm.sections.attachmentsDescription'
                    )}
                >
                    <div className="space-y-5">
                        <div className="space-y-2">
                            <p className="text-xs font-medium">
                                {t(
                                    'roomOverview.mriMachineForm.sections.policies'
                                )}
                            </p>
                            <Controller
                                control={form.control}
                                name="policies"
                                render={({ field }) => (
                                    <PolicyPicker
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                        <div className="space-y-2">
                            <p className="text-xs font-medium">
                                {t(
                                    'roomOverview.mriMachineForm.sections.files'
                                )}
                            </p>
                            <Controller
                                control={form.control}
                                name="attachedFiles"
                                render={({ field }) => (
                                    <FilePicker
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </FormCard>

                {/* Actions */}
                <div
                    className={cn(
                        'flex items-center justify-end gap-3 py-4',
                        actions === 'page' &&
                            'sticky bottom-0 z-10 border-t border-subtle bg-[var(--light-grey)]'
                    )}
                >
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => (onCancel ? onCancel() : navigate(-1))}
                    >
                        {t('roomOverview.mriMachineForm.buttons.cancel')}
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                        {isSaving
                            ? t('roomOverview.mriMachineForm.messages.saving')
                            : t('roomOverview.mriMachineForm.buttons.save')}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
